/**
 * js/modules/registration.js
 * Gestion du formulaire d'inscription en modale avec support des variables d'environnement
 */
import { initTurnstile, loadTurnstileScript } from './turnstile.js';

/**
 * Initialise le formulaire d'inscription en modale et gère les soumissions
 */
export function initRegistrationForm() {
    // Charger le script Turnstile si nécessaire
    loadTurnstileScript();
    
    const form = document.getElementById("notify-form-modal");
    if (!form) return;
  
    const submitButton = document.getElementById("modal-submit-button") || form.querySelector('button[type="submit"]');
    if (!submitButton) return;
    
    const buttonText = submitButton.querySelector(".button-text");
    const spinner = document.getElementById("modal-spinner");
    
    // Créer ou récupérer les éléments de message
    let successMessage = document.getElementById("success-message-modal");
    if (!successMessage) {
        successMessage = document.createElement("div");
        successMessage.id = "success-message-modal";
        successMessage.className = "alert alert-success mt-3";
        successMessage.style.display = "none";
        form.after(successMessage);
    }
    
    let errorMessage = document.getElementById("error-message-modal");
    if (!errorMessage) {
        errorMessage = document.createElement("div");
        errorMessage.id = "error-message-modal";
        errorMessage.className = "alert alert-danger mt-3 d-none";
        form.after(errorMessage);
    }
    
    const emailInput = document.getElementById("email-input-modal");
  
    // Obtenir l'URL de l'API et les paramètres depuis les variables d'environnement
    const apiEndpoint = import.meta.env.VITE_API_ENDPOINT || "https://familystory-registration-form.genealogie.workers.dev";
    const isDevelopment = import.meta.env.VITE_DEVELOPMENT_MODE === 'true';
    const simulateApi = import.meta.env.VITE_SIMULATE_API === 'true';
    
    // Initialiser le captcha Turnstile
    const turnstileContainer = form.querySelector('.cf-turnstile');
    if (turnstileContainer) {
        initTurnstile(turnstileContainer.id || 'turnstile-container');
    }
  
    /**
     * Active/désactive l'état de chargement du bouton
     * @param {boolean} isLoading - Indique si le bouton doit afficher l'état de chargement
     */
    function setLoading(isLoading) {
      if (isLoading) {
        if (buttonText) buttonText.textContent = "Envoi...";
        if (spinner) spinner.classList.remove("d-none");
        submitButton.disabled = true;
        submitButton.classList.add("loading");
      } else {
        if (buttonText) buttonText.textContent = "Je m'inscris";
        if (spinner) spinner.classList.add("d-none");
        submitButton.disabled = false;
        submitButton.classList.remove("loading");
      }
    }
  
    /**
     * Affiche un message de succès
     * @param {string} message - Message à afficher
     */
    function showSuccess(message) {
      successMessage.textContent = message || "Merci ! Vous serez informé dès le lancement de FamilyStory.live.";
      successMessage.style.display = "block";
  
      // Masquer après 5 secondes
      setTimeout(() => {
        successMessage.style.display = "none";
      }, 5000);
    }
  
    /**
     * Affiche un message d'erreur
     * @param {string} message - Message d'erreur à afficher
     */
    function showError(message) {
      errorMessage.textContent = message || "Une erreur est survenue lors de l'inscription. Veuillez réessayer.";
      errorMessage.classList.remove("d-none");
  
      // Masquer après 5 secondes
      setTimeout(() => {
        errorMessage.classList.add("d-none");
      }, 5000);
    }
  
    /**
     * Envoie les données du formulaire à l'API
     * @param {Object} formData - Données du formulaire à envoyer
     * @returns {Promise} Promesse de la requête fetch ou simulée
     */
    function submitFormData(formData) {
      // Uniquement simuler si explicitement configuré pour le faire
      if (simulateApi) {
        console.log("Mode simulation : simulation d'envoi des données", formData);
        
        // Retourner une promesse simulée
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve({
              ok: true,
              json: () => Promise.resolve({ 
                success: true, 
                message: "Inscription simulée réussie" 
              })
            });
          }, 1000);
        });
      }
      
      // Dans tous les autres cas, envoyer réellement les données à l'API
      console.log("Envoi des données au worker:", apiEndpoint);
      
      return fetch(apiEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
    }
  
    // Gestionnaire d'événement pour la soumission du formulaire
    form.addEventListener("submit", async function (e) {
      e.preventDefault();
  
      // Masquer les messages précédents
      successMessage.style.display = "none";
      errorMessage.classList.add("d-none");
  
      // Validation de l'email
      if (!emailInput || !emailInput.value.trim() || !emailInput.checkValidity()) {
        showError("Veuillez entrer une adresse e-mail valide.");
        return;
      }
  
      // Récupérer le token Turnstile
      const turnstileResponse = form.querySelector('[name="cf-turnstile-response"]');
      if (!turnstileResponse || !turnstileResponse.value) {
        showError("Veuillez valider le captcha avant de soumettre le formulaire.");
        return;
      }
  
      // Afficher l'indicateur de chargement
      setLoading(true);
  
      // Préparer les données
      const formData = {
        email: emailInput.value.trim(),
        prelaunch_access: "Oui",
        source: isDevelopment ? "modal-dev" : "modal", // Marquer les inscriptions faites en dev
        timestamp: new Date().toISOString(),
        date_inscription: new Date().toLocaleDateString("fr-FR"),
        heure_inscription: new Date().toLocaleTimeString("fr-FR"),
        page_url: window.location.href,
        cf_turnstile_token: turnstileResponse.value,
      };
  
      try {
        const response = await submitFormData(formData);
  
        if (!response.ok) {
          const errorText = await response.text();
          console.error("Erreur serveur:", response.status, errorText);
          throw new Error("Erreur serveur: " + response.status);
        }
  
        const data = await response.json();
        console.log("Inscription réussie:", data);
  
        // Réinitialiser le formulaire
        emailInput.value = "";
  
        // Réinitialiser Turnstile
        if (typeof turnstile !== "undefined") {
          turnstile.reset();
        }
  
        // Message de succès personnalisé selon l'environnement
        const successMsg = isDevelopment 
          ? "Inscription réussie en mode développement" 
          : "Merci ! Vous serez informé dès le lancement de FamilyStory.live.";
        
        showSuccess(successMsg);
        
        // Fermer la modale après un court délai
        const modal = document.getElementById('signupModal');
        if (window.bootstrap && modal) {
          const modalInstance = bootstrap.Modal.getInstance(modal);
          if (modalInstance) {
            setTimeout(() => {
              modalInstance.hide();
            }, 2000);
          }
        }
      } catch (error) {
        console.error("Erreur lors de l'envoi:", error);
  
        // Réinitialiser Turnstile
        if (typeof turnstile !== "undefined") {
          turnstile.reset();
        }
  
        showError();
      } finally {
        setLoading(false);
      }
    });
}